/**
 * Allowlist of event-property names that may be sent to PostHog
 * (SECURITY_AUDIT.md §5.5). Anything not on this list is dropped.
 *
 * Rules for additions:
 *  - snake_case names.
 *  - No PII — no emails, phone numbers, names, addresses.
 *  - No secrets — no passwords, tokens, verifiers, SSNs, account numbers,
 *    keys, salts, ciphertext.
 *  - Shape: short strings / booleans / numbers only. No objects. If you
 *    find yourself wanting to nest, that's a sign the value is too rich
 *    for analytics and probably logs too much.
 *
 * When in doubt, don't add. An event can ship with no properties and the
 * funnel still works — PostHog will just aggregate by event name.
 */
export const ALLOWED_EVENT_PROPERTIES: ReadonlySet<string> = new Set([
  // Funnel-branch labels — enum-like strings that identify which of
  // several paths a user took.
  'method',               // login: 'device_trust' | 'password_needs_totp'
  'reason',               // login_failed / subscription_* / skip: short label
  'path',                 // email_verified: 'direct_token' | 'api'
  'mode',                 // ai_onboarding: 'quick_start' | 'comprehensive'
  'via',                  // ai_onboarding_chat_started: 'tutorial_complete'
  'from',                 // ai_onboarding_skipped: 'welcome' | 'chat' etc.

  // Wizard step progress
  'step',                 // snake_case step key, e.g., 'profile'
  'number',               // step ordinal

  // Task + doc categorisation (non-identifying — internal keys, not content)
  'task_key',             // e.g., 'documents', 'successors'
  'doc_type',             // e.g., 'will', 'trust'

  // Boolean flags
  'invited',              // signup started from an invitation link
  'used_backup_code',     // TOTP recovery path
  'remember_device',      // TOTP "trust this device 30 days" toggle

  // Counters
  'skips_remaining',      // TOTP skip grace (server returns this)
]);

/**
 * Whether a property name is permitted on events. Dev builds throw on
 * unknown names so instrumentation bugs fail loudly during development.
 * Prod builds silently drop (see `analytics.ts`).
 */
export function isAllowedEventProperty(name: string): boolean {
  return ALLOWED_EVENT_PROPERTIES.has(name);
}
